import { action, runInAction, observable } from 'mobx';
import { format, fromUnixTime } from 'date-fns';
import { ErrorInterface } from 'modules/core/api/globalApi';
import schedulerController from './controller';
import {
  PostOfficeHalfYearInterface,
  UpdatePostOfficeHalfYearInterface,
  CreatePostOfficeHalfYearInterface,
} from '../api/schedulerHalfYear';

export interface SchedulerHalfYearStore {
  halfYear: PostOfficeHalfYearInterface[];
  halfYearByPostOfficeId: string | null;
  loading: boolean;
  error: ErrorInterface | null;
  currentAppointment: PostOfficeHalfYearInterface | null;
}

interface ApiResponse<T = any> {
  data?: T;
  errorData?: ErrorInterface;
}

const initialValues: SchedulerHalfYearStore = {
  halfYear: [],
  halfYearByPostOfficeId: null,
  loading: false,
  error: null,
  currentAppointment: null,
};

export const schedulerHalfYearStore = observable(initialValues);

// Вспомогательная функция (секунды → секунды)
const adjustTimezoneOffset = (timestamp: number): string => {
  const offsetSeconds = new Date().getTimezoneOffset() * 60;
  return (timestamp - offsetSeconds).toString();
};

export const scheduleHalfYearActions = {

  getPostOfficeHalfYear: async (postOfficeId: string): Promise<void> => {
    // Важно: await здесь нужен, чтобы clearError успел отработать, если он асинхронный
    await scheduleHalfYearActions.clearError();
    await scheduleHalfYearActions.clearHalfYear();

    schedulerHalfYearStore.loading = true;
    schedulerHalfYearStore.halfYearByPostOfficeId = postOfficeId;

    try {
      const response: ApiResponse<PostOfficeHalfYearInterface[]> = 
        await schedulerController.getPostOfficeHalfYear(postOfficeId);
      
      scheduleHalfYearActions.handleError(response);

      runInAction(() => {
        schedulerHalfYearStore.halfYear = response.data || [];
        schedulerHalfYearStore.loading = false;
      });
    } catch (error) {
      runInAction(() => { schedulerHalfYearStore.loading = false; });
      throw error;
    }
  },

  generatePostOfficeHalfYear: async (officePostId: string): Promise<void> => {
    await scheduleHalfYearActions.clearError();
    
    const response: ApiResponse = 
      await schedulerController.generatePostOfficeHalfYear({ officePostId });
    
    scheduleHalfYearActions.handleErrorWithoutThrow(response);
    
    // Запускаем обновление списка, но не ждем его (как в оригинале)
    void scheduleHalfYearActions.getPostOfficeHalfYear(officePostId);
  },

  createPostOfficeHalfYear: async (request: CreatePostOfficeHalfYearInterface): Promise<void> => {
    await scheduleHalfYearActions.clearError();
    
    const response: ApiResponse<PostOfficeHalfYearInterface> = 
      await schedulerController.createPostOfficeHalfYear({
        ...request,
        beginDateTime: adjustTimezoneOffset(+request.beginDateTime),
        endDateTime: adjustTimezoneOffset(+request.endDateTime),
      });
    
    scheduleHalfYearActions.handleError(response);

    runInAction(() => {
      if (response.data) {
        // ✅ FIX: Создаем НОВЫЙ массив через spread (аналог cloneDeep для списка)
        schedulerHalfYearStore.halfYear = [...schedulerHalfYearStore.halfYear, response.data];
      }
    });
  },

  updatePostOfficeHalfYear: async (id: string, request: UpdatePostOfficeHalfYearInterface): Promise<void> => {
    await scheduleHalfYearActions.clearError();
    
    const response: ApiResponse = 
      await schedulerController.updatePostOfficeHalfYear(id, {
        ...request,
        beginDateTime: adjustTimezoneOffset(+request.beginDateTime),
        endDateTime: adjustTimezoneOffset(+request.endDateTime),
      });
    
    scheduleHalfYearActions.handleError(response);

    runInAction(() => {
      // ✅ FIX: Используем map, чтобы создать НОВЫЙ массив (новая ссылка)
      // Это гарантирует, что React/MobX увидит изменение
      schedulerHalfYearStore.halfYear = schedulerHalfYearStore.halfYear.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            startDate: format(fromUnixTime(+request.beginDateTime), 'yyyy-MM-dd\'T\'HH:mm:ss'),
            endDate: format(fromUnixTime(+request.endDateTime), 'yyyy-MM-dd\'T\'HH:mm:ss'),
            title: request?.title,
          };
        }
        return item; // Возвращаем старый объект для остальных
      });
    });
  },

  deletePostOfficeHalfYear: async (id: string): Promise<void> => {
    await scheduleHalfYearActions.clearError();
    
    const response: ApiResponse = 
      await schedulerController.deletePostOfficeHalfYear(id);
    
    scheduleHalfYearActions.handleError(response);

    runInAction(() => {
      // filter всегда возвращает новый массив, тут безопасно
      schedulerHalfYearStore.halfYear = schedulerHalfYearStore.halfYear.filter((v) => v.id !== id);
    });
  },

  handleError: action((response: ApiResponse): void => {
    if (response.errorData) {
      schedulerHalfYearStore.error = response.errorData;
      schedulerHalfYearStore.loading = false;
      throw response.errorData;
    }
  }),

  handleErrorWithoutThrow: action((response: ApiResponse): void => {
    if (response.errorData) {
      schedulerHalfYearStore.error = response.errorData;
      schedulerHalfYearStore.loading = false;
    }
  }),

  // ✅ FIX: Вернули async, чтобы не ломать вызовы вида await actions.clearError()
  clearError: async (): Promise<void> => {
    runInAction(() => {
      schedulerHalfYearStore.error = null;
      schedulerHalfYearStore.loading = false;
    });
  },

  // ✅ FIX: Вернули async для совместимости
  clearHalfYear: async (): Promise<void> => {
    runInAction(() => {
      schedulerHalfYearStore.halfYear = [];
      schedulerHalfYearStore.halfYearByPostOfficeId = null;
    });
  },

  setCurrentAppointment: action((appointment: PostOfficeHalfYearInterface | null): void => {
    schedulerHalfYearStore.currentAppointment = appointment;
  }),

  clearCurrentAppointment: action((): void => {
    schedulerHalfYearStore.currentAppointment = null;
  }),

};